import { isEmpty } from "es-toolkit/compat";
import { atom } from "jotai";
import { atomEffect } from "jotai-effect";
import { loadable } from "jotai/utils";

import { Logger } from "@moment/logging";
import { schema } from "@moment/prosemirror-schema";

import { $trackDocumentOpened } from "../analytics";
import { $loggedInUser } from "../authenication";
import { LOCAL_WORKSPACE_NAME } from "../constants";
import { clearDocumentSnapshots } from "../diagnostics-snapshot";
import {
	$document,
	$documentsList,
	docsListCollabEntry,
	docsListItemLocalEntry,
	docsListItemRemoteEntry,
	isViewerPermission,
} from "../document-list";
import { $templateDocuments } from "../document-list/template-documents.atom";
import { $remoteWorkspaceFromDocsApi } from "../document-metadata/document-metadata.atom";
import { metadataKeyForDocsRoute } from "../document-metadata/documentMetadataKey";
import { MetadataNotLoadedError } from "../document-metadata/errors";
import { $loadDocumentMetadataFromDisk } from "../document-metadata/load-metadata-from-disk.atom";
import { $loadDocumentMetadataFromReboot } from "../document-metadata/load-metadata-from-reboot.atom";
import { $loadDocumentMetadataFromRemote } from "../document-metadata/load-metadata-from-remote.atom";
import {
	$documentMetadata,
	$setMetadataStateError,
	$unloadDocumentMetadata,
} from "../document-metadata/metadata-state.atom";
import {
	$ensurePrimaryAgentInstructionFile,
	AGENT_INSTRUCTION_FILES,
	agentInstructionPageId,
} from "../document/agents-md.atom";
import { env } from "../env";
import { $selectedEnvironmentName } from "../environments";
import { $gitStatus } from "../git";
import { $location } from "../location/location.atom";
import {
	$initializeCanvasSnapshotForCollab,
	$loadCanvasSnapshotFromDisk,
	canvasSnapshotKeyForDocsRoute,
} from "../page/page-canvas-state.atom";
import { $unloadEditorState } from "../prosemirror/editor-state.atom";
import { editorStateKeyForDocsRoute } from "../prosemirror/editorStateKey";
import { $loadEditorStateFromDisk } from "../prosemirror/load-editor-state-from-disk.atom";
import { $loadEditorStateFromReboot } from "../prosemirror/load-editor-state-from-reboot.atom";
import { $loadEditorStateFromRemote } from "../prosemirror/load-editor-state-from-remote.atom";
import { $pluginFactory } from "../prosemirror/plugins-factory.atom";
import { $loadHttpRequestFromDisk } from "../request-page/load-http-request-from-disk.atom";
import { $loadHttpRequestFromReboot } from "../request-page/load-http-request-from-reboot.atom";
import { $loadHttpRequestFromRemote } from "../request-page/load-http-request-from-remote.atom";
import { $unloadHttpRequestPage } from "../request-page/request-page.atom";
import { requestPageKeyForDocsRoute } from "../request-page/requestPageKey";
import { $documentSecretsproxyContext } from "../secretsproxy";
import { unwatchMetadataState, watchMetadataState } from "../tracing/metadata-watches";
import { $registeredDocumentIds, $requestedPageLoads } from "./registry.atom";

// Regex to match /d/<workspace name>/<document id> or /e/<workspace name>/<document id>
const DOCS_ROUTE_PATTERN = /^\/[de]\/([^/]+)\/([^/]+)/;

// Regex to extract the page slug from a URL path after the document ID
// e.g., /d/workspace/docId/page-slug/nested → "page-slug/nested"
const PAGE_SLUG_PATTERN = /^\/[de]\/[^/]+\/[^/]+\/(.+)/;

const logger = new Logger("document-loader");

// Tracks keys that have already been loaded (to avoid duplicate loads)
/** @internal */
export const loadedKeys = new Set<string>();

// Tracks the last document ID that fired a document-opened analytics event,
// so that purge-and-reload of the same document doesn't re-fire.
let lastTrackedOpenDocumentId: string | undefined;
const ensuredAgentInstructionDocumentIds = new Set<string>();

/** @internal Reset for testing */
export const resetDocumentOpenTracking = () => {
	lastTrackedOpenDocumentId = undefined;
};

/**
 * An effect that automatically loads a document (metadata, an editor state for all pages, etc.)
 * when either (1) the user visits a well-known URL like `/d/<workspace name>/<document id>`, or (2)
 * the user registers a document as "interesting" via $registerDocument.
 *
 * This effect uses a two-phase loading approach:
 *
 *   1. Load metadata into the new metadata-state system
 *   2. Once metadata is loaded, load editor states for each page
 *
 * Mount this effect by reading it from a component or subscribing via store.sub().
 *
 * !!! IMPORTANT !!!! Load this effect when the app starts, and NEVER UNLOAD IT, EVER!
 */
export const $documentLoaderEffect = atomEffect((get, set) => {
	const registeredIds = get($registeredDocumentIds);
	const location = get($location);
	const loggedInUser = get($loggedInUser);
	const requestedPageLoads = get($requestedPageLoads);
	const selectedEnvironmentLoadable = get(loadable($selectedEnvironmentName));

	// Extract document ID from URL early, before any early returns, so analytics
	// fires for all document types including read-only remote-only documents.
	const match = location.pathname?.match(DOCS_ROUTE_PATTERN);
	const locationWorkspaceName = match?.[1];
	const locationDocumentId = match?.[2];

	// Extract page slug from URL to determine the currently active page
	const pageSlugMatch = location.pathname?.match(PAGE_SLUG_PATTERN);
	const locationPageSlug = pageSlugMatch?.[1];

	// Track document-opened analytics only for URL-based navigation
	// (not for purge-and-reload of the same document or registered template previews)
	if (locationDocumentId && lastTrackedOpenDocumentId !== locationDocumentId) {
		lastTrackedOpenDocumentId = locationDocumentId;
		set($trackDocumentOpened, {
			documentId: locationDocumentId,
			workspaceName: locationWorkspaceName!,
		});
	}

	// Wait for plugin factory to be registered before loading any documents
	const pluginFactory = get($pluginFactory);
	if (!pluginFactory) {
		logger.debug("Waiting for plugin factory to be registered");
		return;
	}

	const docs = get($documentsList);
	if (docs.status === "initializing") {
		logger.debug("Waiting for the cached documents list to load");
		return;
	}

	// Combine registered IDs with current location document
	const documentIds = new Set(registeredIds);
	if (locationDocumentId) {
		documentIds.add(locationDocumentId);
	}

	// Track template document IDs so we can skip SSR cell-stripping for them
	const templateDocuments = get($templateDocuments);

	for (const documentId of documentIds) {
		const documentListItemLoadable = get(loadable($document(documentId)));
		const workspaceLoadable = get(loadable($remoteWorkspaceFromDocsApi({ documentId })));
		const gitStatusLoadable = get(loadable($gitStatus(documentId)));

		// Skip if prerequisite data is still loading or errored
		if (
			documentListItemLoadable.state !== "hasData" ||
			workspaceLoadable.state !== "hasData" ||
			gitStatusLoadable.state !== "hasData"
		) {
			continue;
		}

		const documentListItem = documentListItemLoadable.data;
		const remoteWorkspace = workspaceLoadable.data;
		const gitStatus = gitStatusLoadable.data;

		// Compute the metadata key based on source
		// For collab documents, the key includes branch/sha for proper versioning
		const metadataKey = metadataKeyForDocsRoute(documentId);

		// Check if metadata is already loaded (e.g., from SSR)
		// If so, we don't need to set an error even if the document isn't in the list
		const existingMetadata = get($documentMetadata(metadataKey));
		if (!documentListItem) {
			// Only set error if metadata isn't already loaded
			if (!existingMetadata) {
				const error = new MetadataNotLoadedError();
				logger.error("Document metadata not loaded due to failed pre-conditions", {
					documentId,
					error,
				});
				set($setMetadataStateError, { key: metadataKey, error });
			}
			continue;
		}

		// Determine document source
		const remoteEntry = docsListItemRemoteEntry(documentListItem);
		const isViewerDoc = isViewerPermission(documentListItem);
		// Viewer (read-only) docs always load from remote, even if local files exist
		if (isViewerDoc && !isEmpty(documentListItem.local)) {
			logger.warn("Viewer doc has local files that will be ignored", {
				documentId,
			});
		}
		const hasLocalEntry = !isViewerDoc && !isEmpty(documentListItem.local);
		const hasRemoteEntry = !isEmpty(documentListItem.remote);
		const hasCollab = !isViewerDoc && !isEmpty(remoteEntry?.collab);

		if (hasLocalEntry && !ensuredAgentInstructionDocumentIds.has(documentId)) {
			ensuredAgentInstructionDocumentIds.add(documentId);
			void Promise.resolve()
				.then(() =>
					set($ensurePrimaryAgentInstructionFile, {
						documentId,
					})
				)
				.then((result) => {
					if (result === "failed" || result === "unavailable") {
						ensuredAgentInstructionDocumentIds.delete(documentId);
					}
				})
				.catch((error) => {
					ensuredAgentInstructionDocumentIds.delete(documentId);
					logger.error("Failed to ensure AGENTS.md for local document", {
						documentId,
						error,
					});
				});
		}

		// When a document has both local files and collab (e.g. after a collaborator clones),
		// only use collab mode when on the collab/main branch. Switching to other branches
		// falls back to disk-only loading. Remote-only collab docs always use collab.
		// If the working copy has unresolved conflicts, do not connect to collab — load
		// from disk so the user can resolve conflicts locally first.
		const isOnCollabBranch = gitStatus?.branch === "collab/main";
		const isConflicted = gitStatus?.isConflicted ?? false;
		const shouldUseCollab = hasCollab && (!hasLocalEntry || isOnCollabBranch) && !isConflicted;

		// Get branch/sha from appropriate source:
		// - For collab documents on collab/main: from remoteEntry.collab
		// - For local documents or non-collab branches: from gitStatus
		const collabEntry = shouldUseCollab ? docsListCollabEntry(remoteEntry!) : undefined;
		const workspaceSlug =
			remoteEntry?.workspace.slug ?? remoteWorkspace?.slug ?? LOCAL_WORKSPACE_NAME;
		const branch = collabEntry?.branch ?? gitStatus?.branch;
		const sha = collabEntry?.sha ?? gitStatus?.commit;

		// =======================================================================================
		// PHASE 1: Load metadata based on document source
		// =======================================================================================

		if (!loadedKeys.has(metadataKey)) {
			// Local documents (or local + collab but not on collab branch): load from disk
			if (hasLocalEntry && !shouldUseCollab) {
				logger.info("Loading document metadata from disk", { documentId });
				set($loadDocumentMetadataFromDisk, {
					key: metadataKey,
					document: documentListItem,
				});
				loadedKeys.add(metadataKey);
				watchMetadataState(metadataKey);
			}
			// Documents with collab on collab/main branch: load from Reboot
			else if (shouldUseCollab && workspaceSlug && branch && sha && loggedInUser?.sub) {
				logger.info("Loading document metadata from Reboot", { documentId, metadataKey });

				set($loadDocumentMetadataFromReboot, {
					key: metadataKey,
					workspaceSlug,
					documentId,
					branch,
					sha,
					localEntry: docsListItemLocalEntry(documentListItem),
				});
				loadedKeys.add(metadataKey);
				watchMetadataState(metadataKey);
			}
			// Remote documents without collab: load from DocsAPI (SSR mode)
			else if (hasRemoteEntry) {
				logger.info("Loading document metadata from remote", { documentId });
				set($loadDocumentMetadataFromRemote, {
					key: metadataKey,
					document: documentListItem,
					ssr: true,
				});
				loadedKeys.add(metadataKey);
				watchMetadataState(metadataKey);
			}
		}

		// =======================================================================================
		// PHASE 2: Load editor states for each page (once metadata is loaded)
		// =======================================================================================

		// Read metadata from the NEW metadata-state system
		const metadata = get($documentMetadata(metadataKey));
		if (!metadata) {
			// Metadata not yet loaded - effect will re-run when it becomes available
			continue;
		}

		const pages = metadata.pagesMetadata ?? [];
		const hasRequestPages = pages.some((page) => page.type === "request");
		if (hasRequestPages && selectedEnvironmentLoadable.state === "hasData") {
			get(
				loadable(
					$documentSecretsproxyContext({
						documentId,
						environmentName: selectedEnvironmentLoadable.data,
					})
				)
			);
		}

		// Determine which page is currently active from the URL.
		// For large documents (100+ pages), we defer loading non-active pages
		// until they are explicitly requested (via $requestPageLoad).
		const isLargeDocument = pages.length > 100;
		let currentPageId: string | undefined;
		if (isLargeDocument && locationDocumentId === documentId) {
			// Find the page matching the URL slug
			const slugSegments = (locationPageSlug ?? "").split("/").filter((s) => s.length > 0);
			if (slugSegments.length === 0) {
				// No slug or empty slug → index page or first root page
				const indexPage = pages.find((p) => p.slug === "index" && !p.parentPageID);
				currentPageId = indexPage?.id ?? pages.find((p) => !p.parentPageID)?.id;
			} else {
				// Traverse page tree to find the matching page
				let candidates = pages.filter((p) => !p.parentPageID);
				let found: (typeof pages)[0] | undefined;
				for (const segment of slugSegments) {
					found = candidates.find((p) => p.slug === segment || p.id === segment);
					if (!found) {
						break;
					}
					candidates = pages.filter((p) => p.parentPageID === found!.id);
				}
				currentPageId = found?.id;
			}
		}

		// Use debug-level logging for per-page messages in large documents to avoid
		// overwhelming the WebKit inspector IPC channel with thousands of JSON-serialized
		// console messages (which can crash WebKit's StringBuilder).
		const pageLogger = isLargeDocument ? logger.debug : logger.info;
		let pagesLoadedCount = 0;

		for (const page of pages) {
			const pageId = page.id;
			const pagePath = page.path;

			// For large documents, only load pages that are:
			// 1. The currently active page (from URL)
			// 2. Explicitly requested via $requestPageLoad (on-demand navigation)
			// Small documents (<= 100 pages) load everything immediately for compatibility.
			if (isLargeDocument) {
				const isCurrentPage = currentPageId === pageId;
				const isRequested = requestedPageLoads.has(`${documentId}:${pageId}`);
				if (!isCurrentPage && !isRequested) {
					continue;
				}
			}

			// Handle canvas pages
			if (page.type === "canvas") {
				const canvasKey = canvasSnapshotKeyForDocsRoute({ documentId, pageId });

				// Local documents: load canvas from disk
				if (hasLocalEntry && !shouldUseCollab && !loadedKeys.has(canvasKey)) {
					const documentPath = docsListItemLocalEntry(documentListItem)?.path;
					if (documentPath) {
						pageLogger("Loading canvas snapshot from disk", {
							canvasKey,
							documentId,
							pageId,
						});
						pagesLoadedCount++;
						set($loadCanvasSnapshotFromDisk, {
							key: canvasKey,
							documentId,
							pageId,
							documentPath,
						});
						loadedKeys.add(canvasKey);
					}
				}
				// Collab documents on collab/main: initialize canvas container (tldraw handles its own sync)
				else if (shouldUseCollab && !loadedKeys.has(canvasKey)) {
					pageLogger("Initializing canvas snapshot for collab", {
						canvasKey,
						documentId,
						pageId,
					});
					pagesLoadedCount++;
					set($initializeCanvasSnapshotForCollab, {
						key: canvasKey,
						documentId,
						pageId,
					});
					loadedKeys.add(canvasKey);
				}
				continue;
			}

			// Handle request pages
			if (page.type === "request") {
				const requestKey = requestPageKeyForDocsRoute({ documentId, pageId });

				// Local documents: load from disk
				if (hasLocalEntry && !shouldUseCollab) {
					if (!loadedKeys.has(requestKey)) {
						pageLogger("Loading HTTP request page from disk", {
							requestKey,
							documentId,
							pageId,
						});
						pagesLoadedCount++;
						set($loadHttpRequestFromDisk, {
							key: requestKey,
							document: documentListItem,
							pageId,
							pagePath,
						});
						loadedKeys.add(requestKey);
					}
				}
				// Documents with collab on collab/main: load from Reboot
				else if (shouldUseCollab && workspaceSlug && branch && sha && loggedInUser?.sub) {
					if (!loadedKeys.has(requestKey)) {
						pageLogger("Loading HTTP request page from Reboot", {
							requestKey,
							documentId,
							pageId,
						});
						pagesLoadedCount++;
						set($loadHttpRequestFromReboot, {
							key: requestKey,
							workspaceSlug,
							documentId,
							pageId,
							pagePath,
							branch,
							sha,
							localEntry: docsListItemLocalEntry(documentListItem),
						});
						loadedKeys.add(requestKey);
					}
				}
				// Remote documents without collab: load from DocsAPI (SSR mode)
				else if (hasRemoteEntry) {
					if (!loadedKeys.has(requestKey)) {
						pageLogger("Loading HTTP request page from remote", {
							requestKey,
							documentId,
							pageId,
						});
						pagesLoadedCount++;
						set($loadHttpRequestFromRemote, {
							key: requestKey,
							document: documentListItem,
							pageId,
							pagePath,
							ref: branch ?? "main",
							ssr: true,
						});
						loadedKeys.add(requestKey);
					}
				}
				continue;
			}

			// Skip unknown page types
			if (page.type !== undefined && page.type !== "markdown") {
				continue;
			}

			const editorKey = editorStateKeyForDocsRoute({ documentId, pageId });

			// Local documents: load from disk
			if (hasLocalEntry && !shouldUseCollab) {
				if (!loadedKeys.has(editorKey)) {
					pageLogger("Loading editor state from disk", {
						editorKey,
						documentId,
						pageId: page.id,
					});
					pagesLoadedCount++;
					// Create plugins (no collab version for disk-based documents)
					const readonly = !(env.isDesktopApp || env.isMobileApp);
					const plugins = pluginFactory({
						documentId,
						pageId,
						schema,
						readonly,
						version: undefined,
					});
					set($loadEditorStateFromDisk, {
						key: editorKey,
						document: documentListItem,
						pageId: page.id,
						pagePath,
						plugins,
					});
					loadedKeys.add(editorKey);
				}
			}
			// Documents with collab on collab/main: load from Reboot
			else if (shouldUseCollab && workspaceSlug && branch && sha && loggedInUser?.sub) {
				if (!loadedKeys.has(editorKey)) {
					pageLogger("Loading editor state from Reboot", { editorKey });
					pagesLoadedCount++;
					const readonly = !(env.isDesktopApp || env.isMobileApp);
					set($loadEditorStateFromReboot, {
						key: editorKey,
						workspaceSlug,
						documentId,
						pageId: page.id,
						branch,
						sha,
						pluginFactory,
						schema,
						readonly,
						localEntry: docsListItemLocalEntry(documentListItem),
						userId: loggedInUser.sub,
						pagePath,
					});
					loadedKeys.add(editorKey);
				}
			}
			// Remote documents without collab: load from DocsAPI
			else if (hasRemoteEntry) {
				if (!loadedKeys.has(editorKey)) {
					const isTemplate = documentId in templateDocuments;
					pageLogger("Loading editor state from remote", {
						editorKey,
						documentId,
						pageId: page.id,
						isTemplate,
					});
					pagesLoadedCount++;
					// Create plugins (no collab version for remote read-only documents)
					// Reader-only documents are always read-only, even on desktop
					const readonly = !(env.isDesktopApp || env.isMobileApp) || isViewerDoc;
					const plugins = pluginFactory({
						documentId,
						pageId,
						schema,
						readonly,
						version: undefined,
					});
					// Use "main" as default branch for remote documents without local git status.
					// On desktop/mobile, remote read-only pages must avoid SSR mode because SSR
					// strips compute_cell nodes before the editor state is created. These pages
					// should stay read-only, but code cells still need to mount and run.
					set($loadEditorStateFromRemote, {
						key: editorKey,
						document: documentListItem,
						pageId: page.id,
						pagePath,
						ref: branch ?? "main",
						plugins,
						ssr: !(env.isDesktopApp || env.isMobileApp),
					});
					loadedKeys.add(editorKey);
				}
			}
		}

		// Log a summary for large documents instead of per-page messages
		if (isLargeDocument && pagesLoadedCount > 0) {
			logger.info("Loaded pages for large document", {
				documentId,
				pagesLoadedCount,
				totalPages: pages.length,
			});
		}

		// AGENTS.md collab loading is deferred to useAgentInstructionEditorState.
		// It is NOT eagerly loaded here because for legacy collab documents
		// (shared before this feature), the page authority doesn't exist and
		// the Reboot load would write a PmRebootLoadError that crashes
		// MarkdownPageContent before the disk fallback can run.
	}
});
$documentLoaderEffect.debugLabel = "documentLoaderEffect";

/**
 * Purges all loaded state for a document, allowing the loader effect to reload it.
 * Call this when a document transitions from local-only to collab (e.g., after sharing).
 */
export const $purgeDocument = atom(null, (get, set, documentId: string) => {
	const metadataKey = metadataKeyForDocsRoute(documentId);
	const metadata = get($documentMetadata(metadataKey));
	const pages = metadata?.pagesMetadata ?? [];
	ensuredAgentInstructionDocumentIds.delete(documentId);

	// Remove keys from loadedKeys set
	loadedKeys.delete(metadataKey);
	for (const page of pages) {
		if (page.type === "request") {
			const requestKey = requestPageKeyForDocsRoute({ documentId, pageId: page.id });
			loadedKeys.delete(requestKey);
		} else {
			const editorKey = editorStateKeyForDocsRoute({ documentId, pageId: page.id });
			loadedKeys.delete(editorKey);
		}
	}

	// Clear requested page loads for this document so lazy loading
	// doesn't eagerly reload all previously-visited pages after purge.
	const currentRequested = get($requestedPageLoads);
	const prefix = `${documentId}:`;
	const hasStaleEntries = [...currentRequested].some((key) => key.startsWith(prefix));
	if (hasStaleEntries) {
		const cleaned = new Set([...currentRequested].filter((key) => !key.startsWith(prefix)));
		set($requestedPageLoads, cleaned);
	}

	// Unload page states first (they depend on metadata)
	for (const page of pages) {
		if (page.type === "request") {
			const requestKey = requestPageKeyForDocsRoute({ documentId, pageId: page.id });
			set($unloadHttpRequestPage, requestKey);
		} else {
			const editorKey = editorStateKeyForDocsRoute({ documentId, pageId: page.id });
			set($unloadEditorState, editorKey);
		}
	}

	// Unload agent instruction file editor states (outside pagesMetadata)
	for (const filename of AGENT_INSTRUCTION_FILES) {
		const pageId = agentInstructionPageId(filename);
		const editorKey = editorStateKeyForDocsRoute({ documentId, pageId });
		loadedKeys.delete(editorKey);
		set($unloadEditorState, editorKey);
	}

	// Stop tracing before unloading
	unwatchMetadataState(metadataKey);

	// Unload metadata
	set($unloadDocumentMetadata, metadataKey);

	// Clear diagnostics snapshot cache for this document
	clearDocumentSnapshots(documentId);

	logger.info("Purged document state", { documentId, metadataKey, pageCount: pages.length });
});
$purgeDocument.debugLabel = "purgeDocument";
